import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Sparkles, X, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const STORAGE_KEYS = {
  trial: "1o1_trial_banner_dismissed",
  expiring: "1o1_expiring_banner_dismissed",
};

// Show a different style banner depending on user/premium state.
// Auto-dismissable; remembers dismissal per day in localStorage.
const TopBanner = () => {
  const { user, premium } = useAuth();
  const location = useLocation();
  const [dismissed, setDismissed] = useState({});

  // Hide on premium page, account page, public playlist share view
  const hide =
    location.pathname.startsWith("/premium") ||
    location.pathname.startsWith("/account") ||
    location.pathname.startsWith("/p/");

  useEffect(() => {
    const today = new Date().toDateString();
    const d = {};
    Object.entries(STORAGE_KEYS).forEach(([k, key]) => {
      d[k] = localStorage.getItem(key) === today;
    });
    setDismissed(d);
  }, []);

  const dismiss = (kind) => {
    const today = new Date().toDateString();
    localStorage.setItem(STORAGE_KEYS[kind], today);
    setDismissed((p) => ({ ...p, [kind]: true }));
  };

  if (hide) return null;

  // Variant 1: Free or unauthenticated → promote trial
  const showTrial =
    !premium?.active && !premium?.trial_used && !dismissed.trial;

  // Variant 2: Active premium expiring in ≤3 days OR cancel_at_period_end with ≤7 days left → renew nudge
  const showExpiring =
    premium?.active &&
    !dismissed.expiring &&
    ((premium.cancel_at_period_end && premium.days_left <= 7) ||
      (!premium.cancel_at_period_end && premium.days_left <= 3));

  if (!showTrial && !showExpiring) return null;

  if (showExpiring) {
    const ending = premium.cancel_at_period_end;
    return (
      <div
        data-testid="banner-expiring"
        className="sticky top-0 z-30 -mx-4 sm:-mx-8 px-4 sm:px-8 py-2.5 bg-gradient-to-r from-[#FF2E00]/20 via-[#FF2E00]/15 to-[#FF2E00]/20 border-b border-[#FF2E00]/30 backdrop-blur-2xl flex items-center justify-between gap-3"
      >
        <div className="flex items-center gap-3 min-w-0">
          <AlertTriangle size={16} className="text-[#FF2E00] flex-shrink-0" />
          <p className="text-sm truncate">
            <span className="font-semibold">
              {ending ? "Your Premium ends" : "Premium expires"} in {premium.days_left} day
              {premium.days_left === 1 ? "" : "s"}.
            </span>{" "}
            <span className="text-white/70 hidden sm:inline">
              Renew to keep unlimited playlists & ad-free listening.
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link
            to="/account"
            data-testid="banner-expiring-cta"
            className="bg-[#FF2E00] hover:bg-[#E62900] text-white text-xs font-semibold rounded-full px-4 py-1.5 transition whitespace-nowrap"
          >
            Renew now
          </Link>
          <button
            data-testid="banner-expiring-dismiss"
            onClick={() => dismiss("expiring")}
            className="p-1.5 text-white/50 hover:text-white"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  // Trial promo
  return (
    <div
      data-testid="banner-trial"
      className="sticky top-0 z-30 -mx-4 sm:-mx-8 px-4 sm:px-8 py-2.5 bg-gradient-to-r from-[#FF2E00]/15 via-[#FF2E00]/15 to-[#E2FF31]/10 border-b border-white/10 backdrop-blur-2xl flex items-center justify-between gap-3"
    >
      <div className="flex items-center gap-3 min-w-0">
        <Sparkles size={14} className="text-[#E2FF31] flex-shrink-0" />
        <p className="text-sm truncate">
          <span className="font-semibold">2 months free</span>
          <span className="text-white/70"> · Premium, then $10/month — cancel any time</span>
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Link
          to="/premium"
          data-testid="banner-trial-cta"
          className="bg-white text-black text-xs font-semibold rounded-full px-4 py-1.5 hover:scale-[1.04] transition whitespace-nowrap"
        >
          {user ? "Start trial" : "Get Premium"}
        </Link>
        <button
          data-testid="banner-trial-dismiss"
          onClick={() => dismiss("trial")}
          className="p-1.5 text-white/50 hover:text-white"
          aria-label="Dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

export default TopBanner;
